import { TERPENES } from '../constants/labCompounds';
import { terpenesMeta } from '@wysiwyg/moss-runtime';

type CompoundField = { family: string; suffix: string };

const normalizeCompoundSlug = (name: string): string => name.replace(/_([a-z])/g, (_, c: string) => c.toUpperCase()).replace(/^a([A-Z])/, 'alpha$1').replace(/^b([A-Z])/, 'beta$1').replace(/^g([A-Z])/, 'gamma$1')
    .replace(/α/gi, 'alpha').replace(/β/gi, 'beta').replace(/γ/gi, 'gamma').toLowerCase().replace(/[^a-z0-9]/g, '');

// Runtime metadata declares historical terpene aliases by sharing one canonical object.
const terpeneFamilies = new Map<string, string>();
const canonicalTerpenes = new Map<object, string>();
for (const [key, meta] of Object.entries(terpenesMeta)) {
  if (!meta || typeof meta !== 'object') continue;
  const normalized = normalizeCompoundSlug(key);
  const canonical = canonicalTerpenes.get(meta) ?? normalized;
  canonicalTerpenes.set(meta, canonical);
  terpeneFamilies.set(normalized, canonical);
  // Older markers also stored the raw ASCII prefix after case folding.
  terpeneFamilies.set(normalizeCompoundSlug(key.toLowerCase()), canonical);
}

// Some supported editor keys are absent from the pinned runtime metadata. Keep
// their historical case-folded markers in the same family without a second list.
for (const key of TERPENES) {
  const normalized = normalizeCompoundSlug(key);
  const canonical = terpeneFamilies.get(normalized) ?? normalized;
  if (!terpeneFamilies.has(normalized)) terpeneFamilies.set(normalized, canonical);
  const historical = normalizeCompoundSlug(key.toLowerCase());
  if (!terpeneFamilies.has(historical)) terpeneFamilies.set(historical, canonical);
}

/** Classify only declared runtime/editor terpene families; a marker cannot invent one. */
export function supportedTerpeneKey(name: string): string | undefined {
  const family = terpeneFamilies.get(normalizeCompoundSlug(name));
  return family ? TERPENES.find(key => terpeneFamilies.get(normalizeCompoundSlug(key)) === family) : undefined;
}

/** Match only the existing compound-marker protocol, including historical aliases. */
export function manualCompoundField(name: string): CompoundField | undefined {
  const match = /^lab_(cb|tp)_(.+)_(override|weightMgG|weightMl|weightPerUnit|weightBasis|weightPkg|weightSrv|uom|percent)$/.exec(name);
  if (!match || (match[1] === 'tp' && /^(weightMl|weightPerUnit)$/.test(match[3]))) return undefined;
  const normalized = normalizeCompoundSlug(match[2]);
  const slug = match[1] === 'tp' ? terpeneFamilies.get(normalized) ?? normalized : normalized;
  return { family: `${match[1].toLowerCase()}:${slug}`, suffix: match[3].toLowerCase() };
}

/** Ambiguous saved aliases remain removals until a user explicitly changes them. */
export function manualOverrideState(value: unknown, enabledKey: string): boolean | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;
  const target = manualCompoundField(enabledKey);
  let enabled: boolean | undefined;
  for (const [key, flag] of Object.entries(value)) {
    if (typeof flag !== 'boolean') continue;
    const candidate = manualCompoundField(key);
    const matches = target ? candidate?.family === target.family && candidate.suffix === 'override'
      : key === enabledKey;
    if (!matches) continue;
    if (flag === false) return false;
    enabled = true;
  }
  return enabled;
}

/** Whether a measurement field was authored, separately from inferred editor hydration. */
export function hasManualCompoundField(value: unknown, enabledKey: string, suffix: string): boolean {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const family = manualCompoundField(enabledKey)?.family;
  return Boolean(family) && Object.entries(value).some(([key, marker]) => {
    // Public payloads retain only boolean authorship, never private measurements.
    // False/malformed flags cannot claim a field; aliases still use one family.
    const authored = /^lab_tp_.+_(weightSrv|weightPkg)_authored$/.test(key);
    const field = manualCompoundField(authored ? key.slice(0, -'_authored'.length) : key);
    return field?.family === family && field?.suffix === suffix.toLowerCase() && (!authored || marker === true);
  });
}

/** An exact canonical saved entry wins aliases, including deliberate empty values. */
export function manualCompoundValue(value: unknown, canonicalKey: string, enabledKey: string): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  const compounds = value as Record<string, unknown>;
  const family = manualCompoundField(enabledKey)?.family;
  if (!family) return {};
  const prefix = family?.startsWith('tp:') ? 'lab_tp_' : 'lab_cb_';
  const key = Object.prototype.hasOwnProperty.call(compounds, canonicalKey) ? canonicalKey
    : Object.keys(compounds).find(key => manualCompoundField(`${prefix}${key}_override`)?.family === family);
  const result = key ? compounds[key] : undefined;
  return result && typeof result === 'object' && !Array.isArray(result) ? result as Record<string, unknown> : {};
}

/** Read authored field intent with the same canonical-first family protocol. */
export function manualCompoundFieldValue(value: unknown, enabledKey: string, suffix: string): unknown {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;
  const fields = value as Record<string, unknown>;
  const prefix = enabledKey.replace(/_override$/, '');
  const canonical = `${prefix}_${suffix}`;
  if (Object.prototype.hasOwnProperty.call(fields, canonical)) return fields[canonical];
  const family = manualCompoundField(enabledKey)?.family;
  return Object.entries(fields).find(([key]) => {
    const field = manualCompoundField(key);
    return family && field?.family === family && field.suffix === suffix.toLowerCase();
  })?.[1];
}

/** A present primary family owns the value, including null, empty and cleared entries. */
export function manualCompoundSource(primary: unknown, fallback: unknown, enabledKey: string): Record<string, unknown> {
  const record = (value: unknown): Record<string, unknown> => value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {};
  const compounds = record(primary);
  const family = manualCompoundField(enabledKey)?.family;
  const prefix = family?.startsWith('tp:') ? 'lab_tp_' : 'lab_cb_';
  return family && Object.keys(compounds).some(key => manualCompoundField(`${prefix}${key}_override`)?.family === family)
    ? compounds : record(fallback);
}
